import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { checkForNewVersion, useStaleVersion } from "@/lib/stale-version";

/** Re-asked on this cadence even if the tab is never blurred or erroring. */
const POLL_INTERVAL_MS = 15 * 60_000;

/**
 * Floats above everything once the tab is known to be running a stale bundle,
 * and owns the schedule of asking. Deliberately NOT a toast: a toast expires,
 * and this stays true until the person reloads. Deliberately not in the app
 * shell either — a stale login page misleads just as well as a stale dashboard,
 * so it mounts globally in main.tsx.
 *
 * Fixed to the bottom centre: the sticky header and the top-16 sidebar offsets
 * assume a 64px chrome height, and toasts already occupy the bottom-right.
 */
export function StaleVersionBanner() {
  const stale = useStaleVersion((s) => s.stale);

  useEffect(() => {
    void checkForNewVersion();
    const onVisible = () => {
      if (document.visibilityState === "visible") void checkForNewVersion();
    };
    const interval = window.setInterval(() => void checkForNewVersion(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  if (!stale) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 shadow-pop"
    >
      <Icon name="refresh" size={20} className="text-accent" />
      <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink">
        Compass has been updated. Refresh to load the new version.
      </p>
      <Button size="sm" variant="primary" onClick={() => window.location.reload()}>
        Refresh
      </Button>
    </div>
  );
}
